// SAB-425: Public NFL Leagues browse page.
//
// Lists every league where:
//   sport            implicit (nfl_leagues is NFL-only)
//   visibility       = 'public'
//   status           in ('setup', 'pre_draft')   ← drops off once drafting
//   open seats       > 0                          ← drops off once full
//
// Sort: most-filled first (social proof + fastest-to-start), secondary
// sort newest. Brand voice mirror Home/Landing — irreverent, blunt.
//
// Joining: clicking the card routes to /nfl/join/browse/:leagueId, which
// shows the existing buy-in confirm + join flow but POSTs { league_id }
// instead of { invite_code }. The edge function gates on visibility='public'
// before invoking the atomic RPC.
//
// This is a read of public data — RLS allows anon select on nfl_leagues for
// browse-eligible rows. Client-side supabase-js select is fine for v1.
//
// SAB-558 (interim): prod nfl_leagues is missing the `visibility` column, so
// the browse query below errors ("column nfl_leagues.visibility does not
// exist"). Until that migration ships, the whole data path is gated behind
// VITE_PUBLIC_LEAGUES_ENABLED (default off): when off we render a coming-soon
// state with ZERO network calls; when on (post-fix) the query path runs
// unchanged. Raw DB/column errors are never rendered to users — console only.

import { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { supabase } from '@/integrations/supabase/client'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Loader2, Users, Gavel, DollarSign, Bomb, ChevronRight,
  Search, PlusCircle,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { prizeScheduleSummary, type PrizeScheduleState } from '@/components/nfl/PrizeScheduleDisclosure'

// SAB-558 interim flag. Default off (unset). Set VITE_PUBLIC_LEAGUES_ENABLED='true'
// only after the nfl_leagues.visibility migration has shipped to the target env.
const PUBLIC_LEAGUES_ENABLED = import.meta.env.VITE_PUBLIC_LEAGUES_ENABLED === 'true'

interface BrowseLeague {
  id: string
  name: string
  max_teams: number
  filled_teams: number
  entry_fee: number
  scoring_type: string | null
  draft_type: string | null
  status: string
  // Whether sabotage cards are enabled — surfaced as a Yes/No badge.
  // scoring_type='sabotage' is the proxy today; SAB has no separate flag.
  sabotage_enabled: boolean
  // SAB-632 pre-join disclosure: compact payout summary ("Pays top 3 · 50%/30%/20%").
  // Null when the league has no percentage structure to show.
  prize_summary: string | null
}

export default function NFLLeaguesBrowsePage() {
  const navigate = useNavigate()
  const [leagues, setLeagues] = useState<BrowseLeague[] | null>(null)
  // Boolean only — we never store or render the raw error message.
  const [error, setError] = useState<boolean>(false)

  useEffect(() => {
    // SAB-558 interim: while gated off, make no request to nfl_leagues at all.
    if (!PUBLIC_LEAGUES_ENABLED) return
    let cancelled = false
    async function load() {
      // SAB-425: nfl_teams!inner aggregation through a separate count-call
      // would let us push the "open seats > 0" filter to the DB, but for v1
      // the simpler approach is fetch all public + pre-draft/setup leagues
      // (max ~hundreds, sort + filter client-side). Switch to a Postgres
      // VIEW if this list ever exceeds ~500 rows.
      // Disambiguate the nfl_teams embed: PostgREST sees two FK paths
      // between nfl_leagues ↔ nfl_teams — the forward path we want
      // (`nfl_teams.league_id → nfl_leagues.id`) and a reverse path
      // (`nfl_leagues.champion_team_id → nfl_teams.id`) that predates
      // this work. Without an explicit FK hint, PostgREST refuses to
      // resolve and the browse page bombs with "more than one
      // relationship was found". Name the FK constraint exactly so the
      // resolver joins through league_id.
      const { data, error: qErr } = await (supabase as any)
        // SAB security H3: read the safe public-browse view, not nfl_leagues directly
        // (the table no longer exposes invite_code / private leagues to non-members).
        .from('nfl_public_leagues')
        .select('id, name, max_teams, entry_fee, scoring_type, draft_type, status, filled_teams, created_at')
        .in('status', ['setup', 'pre_draft'])
        .order('created_at', { ascending: false })
      if (cancelled) return
      if (qErr) {
        // Never surface raw DB/column errors to users — console only.
        console.error('[NFLLeaguesBrowsePage] failed to load public leagues:', qErr)
        setError(true)
        return
      }
      // SAB-632 pre-join disclosure: batch-fetch payout structures for the listed
      // leagues and build the compact per-card summary line.
      const ids = (data ?? []).map((r: any) => r.id)
      const summaryByLeague = new Map<string, string | null>()
      if (ids.length > 0) {
        const { data: schedRows } = await (supabase as any)
          .from('nfl_public_league_prize_schedules')
          .select('league_id, state, label, final_rank, allocation_pct, resolved_amount, frozen_pool')
          .in('league_id', ids)
        if (cancelled) return
        const byLeague = new Map<string, any[]>()
        for (const r of schedRows ?? []) {
          if (!byLeague.has(r.league_id)) byLeague.set(r.league_id, [])
          byLeague.get(r.league_id)!.push(r)
        }
        for (const [lid, rows] of byLeague) {
          const state: PrizeScheduleState = rows[0]?.state === 'frozen' ? 'frozen' : 'provisional'
          summaryByLeague.set(lid, prizeScheduleSummary(state, rows))
        }
      }
      const rows: BrowseLeague[] = (data ?? [])
        .map((row: any) => {
          const filled = Number(row.filled_teams ?? 0)
          return {
            id: row.id,
            name: row.name,
            max_teams: row.max_teams,
            filled_teams: filled,
            entry_fee: Number(row.entry_fee ?? 0),
            scoring_type: row.scoring_type,
            draft_type: row.draft_type,
            status: row.status,
            sabotage_enabled: row.scoring_type === 'sabotage',
            prize_summary: summaryByLeague.get(row.id) ?? null,
          }
        })
        // Hide full leagues (open seats > 0 requirement)
        .filter(l => l.filled_teams < l.max_teams)
        // Sort: most-filled first (= fewest seats remaining), tiebreak newest
        // already enforced by the .order('created_at') above.
        .sort((a, b) => (b.filled_teams / b.max_teams) - (a.filled_teams / a.max_teams))
      setLeagues(rows)
    }
    load()
    return () => { cancelled = true }
  }, [])

  return (
    <div className="min-h-screen bg-gradient-dark px-4 py-6">
      <div className="max-w-3xl mx-auto space-y-4">
        {/* Header */}
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Search className="h-5 w-5 text-primary" />
            <h1 className="text-xl font-display font-bold text-foreground">
              Public Leagues
            </h1>
          </div>
          <p className="text-sm text-muted-foreground">
            Open seats, no invite code needed. Pick one and play.
          </p>
        </div>

        {/* SAB-558 interim: coming-soon when gated off (no query runs); the live
            browse/loading/error/empty/list path only mounts when the flag is on. */}
        {!PUBLIC_LEAGUES_ENABLED ? (
          <Card className="bg-card border-border">
            <CardContent className="p-12 text-center space-y-4">
              <div className="h-14 w-14 mx-auto rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                <Search className="h-6 w-6 text-primary" />
              </div>
              <p className="text-lg font-display font-bold text-primary">
                Public leagues coming soon!
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Loading */}
            {leagues === null && !error && (
              <Card className="bg-card border-border">
                <CardContent className="p-12 flex items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </CardContent>
              </Card>
            )}

            {/* Error — generic copy only; the raw error is logged to the console. */}
            {error && (
              <Card className="bg-card border-border">
                <CardContent className="p-6 text-center">
                  <p className="text-sm text-red-400">
                    Couldn't load leagues right now. Try again shortly.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Empty state */}
            {leagues !== null && leagues.length === 0 && !error && (
              <Card className="bg-card border-border">
                <CardContent className="p-8 text-center space-y-4">
                  <div className="h-14 w-14 mx-auto rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                    <Search className="h-6 w-6 text-primary" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-base font-semibold text-foreground">
                      No public leagues with open seats right now.
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Start one yourself. People will join.
                    </p>
                  </div>
                  <Button
                    onClick={() => navigate('/nfl/create')}
                    className="bg-primary hover:bg-primary/90"
                  >
                    <PlusCircle className="h-4 w-4 mr-2" />
                    Create a League
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* List */}
            {leagues !== null && leagues.length > 0 && (
              <div className="space-y-3">
                {leagues.map(l => (
                  <LeagueCard key={l.id} league={l} onJoin={() => navigate(`/nfl/join/browse/${l.id}`)} />
                ))}
              </div>
            )}
          </>
        )}

        {/* Footer */}
        <div className="pt-2">
          <Link to="/nfl" className="text-xs text-muted-foreground hover:text-foreground transition-colors">
            ← Back to your leagues
          </Link>
        </div>
      </div>
    </div>
  )
}

function LeagueCard({ league, onJoin }: { league: BrowseLeague; onJoin: () => void }) {
  const seatsLeft = league.max_teams - league.filled_teams
  const fillPct = (league.filled_teams / league.max_teams) * 100
  return (
    <Card className="bg-card border-border hover:border-primary/30 transition-colors">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-sm font-display font-bold text-foreground truncate">{league.name}</h3>
            {/* SAB-632 pre-join disclosure summary; the full schedule renders on the join preview. */}
            {league.prize_summary && (
              <p className="text-[10px] font-semibold text-primary mt-0.5">{league.prize_summary}</p>
            )}
            <p className="text-[10px] text-muted-foreground mt-0.5">
              {league.filled_teams}/{league.max_teams} teams · {seatsLeft} {seatsLeft === 1 ? 'spot' : 'spots'} open
            </p>
          </div>
          <Badge
            variant="outline"
            className={cn(
              'text-[9px] shrink-0',
              fillPct >= 75 ? 'border-amber-500/40 text-amber-400'
              : fillPct >= 50 ? 'border-primary/30 text-primary'
              : 'border-border text-muted-foreground'
            )}
          >
            {fillPct >= 75 ? 'Almost full' : fillPct >= 50 ? 'Filling up' : 'Open'}
          </Badge>
        </div>

        {/* Fill bar */}
        <div className="h-1.5 rounded-full bg-muted/40 overflow-hidden">
          <div
            className={cn(
              'h-full transition-all',
              fillPct >= 75 ? 'bg-amber-500' : 'bg-primary',
            )}
            style={{ width: `${Math.min(100, fillPct)}%` }}
          />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-2">
          <Stat icon={<DollarSign className="h-3 w-3" />} label="Buy-in" value={league.entry_fee > 0 ? `$${league.entry_fee}` : 'Free'} />
          <Stat icon={<Gavel className="h-3 w-3" />} label="Draft" value={league.draft_type === 'snake' ? 'Live' : 'Slow'} />
          <Stat icon={<Users className="h-3 w-3" />} label="Size" value={`${league.max_teams}`} />
          <Stat icon={<Bomb className="h-3 w-3" />} label="Sabotage" value={league.sabotage_enabled ? 'On' : 'Off'} />
        </div>

        <Button
          onClick={onJoin}
          className="w-full bg-primary hover:bg-primary/90 h-9 text-sm"
        >
          Join
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </CardContent>
    </Card>
  )
}

function Stat({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="p-2 rounded-md bg-muted/30 border border-border/60 text-center">
      <div className="flex items-center justify-center text-muted-foreground mb-0.5">{icon}</div>
      <p className="text-xs font-bold text-foreground leading-tight">{value}</p>
      <p className="text-[9px] text-muted-foreground uppercase tracking-wide">{label}</p>
    </div>
  )
}
